"use client"

import { useLayoutEffect, useRef, useState } from "react"

/** One-line title. Tries each candidate in order and shows the first that fits without cutting anything, stepping the
 * font down from 19px to 15px before moving on to the next. The last candidate is always a short one that fits, so the
 * header never breaks and a name is never cut down to a few letters. */
export function FitTitle({ candidates }: { candidates: string[] }) {
  const wrapRef = useRef<HTMLDivElement>(null)
  const measureRef = useRef<HTMLSpanElement>(null)
  const [choice, setChoice] = useState<{ text: string; size: number }>({ text: candidates[candidates.length - 1] ?? "", size: 19 })
  const key = candidates.join("\u0000")

  useLayoutEffect(() => {
    const wrap = wrapRef.current
    const measure = measureRef.current
    if (!wrap || !measure) return
    const options = key.split("\u0000")
    const fit = () => {
      const available = wrap.clientWidth
      for (const text of options) {
        measure.textContent = text
        for (let px = 19; px >= 15; px -= 0.5) {
          measure.style.fontSize = px + "px"
          if (measure.scrollWidth <= available) {
            setChoice((prev) => (prev.text === text && prev.size === px ? prev : { text, size: px }))
            return
          }
        }
      }
      const last = options[options.length - 1] ?? ""
      setChoice((prev) => (prev.text === last && prev.size === 15 ? prev : { text: last, size: 15 }))
    }
    fit()
    const ro = new ResizeObserver(fit)
    ro.observe(wrap)
    // Web fonts load after first paint and change the text width, so measure again once they're ready.
    void document.fonts?.ready.then(fit)
    return () => ro.disconnect()
  }, [key])

  return (
    <div ref={wrapRef} className="relative min-w-0">
      <h2 title={candidates[0]} style={{ fontSize: choice.size }} className="block overflow-hidden text-ellipsis whitespace-nowrap font-[750] leading-tight">
        {choice.text}
      </h2>
      {/* Invisible copy used only to measure how wide each candidate would be. */}
      <span ref={measureRef} aria-hidden="true" className="invisible absolute left-0 top-0 whitespace-nowrap font-[750] leading-tight" />
    </div>
  )
}
